/**
 * CpLegend.tsx — CP1–CP10 legend panel
 *
 * Zone 2 of the DNA surface.
 * Mirrors the helix — two views of the same data.
 *
 * Click a row → helix auto-orients to that CP + Q&A panel opens.
 */

import { HELIX_CHECKPOINTS } from "@/lib/helix-checkpoints";
import { getHealthColor } from "@shared/schema";

interface Props {
  cpScores: Record<string, number>; // { cp1: 75, ... }
  selectedCp: number | null;
  onSelectCp: (cp: number | null) => void;
}

// Per-CP scores → canon palette per CANON.md §5 (@shared/schema).
function scoreColor(pct: number): string {
  return getHealthColor(pct);
}
function scoreBg(pct: number): string {
  return `${getHealthColor(pct)}1F`; // ~12% alpha tint
}
function statusDot(pct: number) {
  const color = scoreColor(pct);
  return (
    <span
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: color,
        flexShrink: 0,
        boxShadow: `0 0 6px ${color}88`,
      }}
    />
  );
}

export default function CpLegend({ cpScores, selectedCp, onSelectCp }: Props) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#6B7280",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          padding: "0 4px 10px",
          borderBottom: "1px solid #E5E7EB",
          marginBottom: 8,
          flexShrink: 0,
        }}
      >
        Checkpoints
      </div>

      {/* CP rows — rendered CP10 → CP1 (top to bottom) to mirror the 3D helix
           bottom-up canon: CP10 = rooftop, CP1 = waterline. W60. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
        {[...HELIX_CHECKPOINTS].reverse().map((cp) => {
          const rawPct = cpScores[cp.id] ?? -1;
          // -1 means zero answered questions — render as "—"
          const pct = rawPct < 0 ? 0 : rawPct;
          const hasScore = rawPct >= 0;
          const color = hasScore ? scoreColor(pct) : "#6B6B70";
          const isSel = selectedCp === cp.number;

          return (
            <button
              key={cp.id}
              onClick={() => onSelectCp(isSel ? null : cp.number)}
              title={`${cp.fullName} — ${pct}%`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 10px",
                borderRadius: 8,
                border: isSel
                  ? `1px solid ${color}55`
                  : "1px solid transparent",
                background: isSel
                  ? scoreBg(pct)
                  : "transparent",
                cursor: "pointer",
                textAlign: "left",
                width: "100%",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                if (!isSel) {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "#F3F4F6";
                  (e.currentTarget as HTMLButtonElement).style.borderColor =
                    "#E5E7EB";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSel) {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "transparent";
                  (e.currentTarget as HTMLButtonElement).style.borderColor =
                    "transparent";
                }
              }}
            >
              {/* CP number badge */}
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  background: scoreBg(pct),
                  border: `1.5px solid ${color}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 800,
                  color,
                  flexShrink: 0,
                }}
              >
                {cp.number}
              </div>

              {/* Name */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: isSel ? "#111827" : "#374151",
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {cp.name}
                </div>
              </div>

              {/* Score */}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color,
                  fontVariantNumeric: "tabular-nums",
                  flexShrink: 0,
                }}
              >
                {hasScore ? `${pct}%` : "\u2014"}
              </span>

              {/* Status dot */}
              {statusDot(pct)}
            </button>
          );
        })}
      </div>

      {/* Deselect hint when something is selected — W66.Q Fix 6: light token */}
      {selectedCp !== null && (
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: "1px solid #E5E7EB",
            fontSize: 10,
            color: "#6B7280",
            textAlign: "center",
            flexShrink: 0,
          }}
        >
          Click again to deselect
        </div>
      )}
    </div>
  );
}
